import { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useDeploymentParameters } from '../hooks/useDeploymentParameters';
import { DriftReason } from '../../../types/backend';
import type { Deployment } from '../../../types/backend';
import type { ParameterSource } from '../types';

interface DeploymentRowParametersProps {
  customerId: string;
  deployment: Deployment;
  onRefresh: () => void;
}

const SOURCE_LABEL: Record<ParameterSource, string> = {
  literal: 'literal',
  resolver: 'resolver',
  'operator-input': 'operator input',
};

const SOURCE_CHIP_COLOR: Record<ParameterSource, 'default' | 'info' | 'warning'> = {
  literal: 'default',
  resolver: 'info',
  'operator-input': 'warning',
};

const DeploymentRowParameters = memo<DeploymentRowParametersProps>(({
  customerId,
  deployment,
  onRefresh,
}) => {
  const {
    values,
    errors,
    submitting,
    preview,
    previewLoading,
    previewUnavailable,
    setValue,
    clearValue,
    submit,
    snackbar,
    closeSnackbar,
  } = useDeploymentParameters({
    customerId,
    deployment,
    onSuccess: onRefresh,
  });

  if (previewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (previewUnavailable || !preview) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Parameter preview unavailable. Auto-resolved values will be applied at launch.
      </Typography>
    );
  }

  const sourcesEntries = Object.entries(preview.sources ?? {}) as Array<[string, ParameterSource]>;
  const operatorInputKeys = sourcesEntries
    .filter(([, src]) => src === 'operator-input')
    .map(([k]) => k);
  const readOnlyEntries = sourcesEntries.filter(([, src]) => src !== 'operator-input');

  const missingRequired = deployment.drift_reason === DriftReason.MISSING_REQUIRED_PARAMETERS;
  const unresolvedKeys = preview.unresolved ?? [];

  const desiredParams = (deployment.desired_parameters ?? {}) as Record<string, unknown>;
  const actualParams = (deployment.actual_parameters ?? {}) as Record<string, unknown>;

  function isDrifted(key: string): boolean {
    if (!(key in desiredParams) || !(key in actualParams)) return false;
    return String(desiredParams[key]) !== String(actualParams[key]);
  }

  return (
    <Box sx={{ py: 1 }}>
      {missingRequired && unresolvedKeys.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Blocked — missing required operator input
          </Typography>
          <Typography variant="body2">
            {unresolvedKeys.join(', ')}
          </Typography>
        </Alert>
      )}

      {readOnlyEntries.length > 0 && (
        <Box
          sx={{
            mb: operatorInputKeys.length > 0 ? 2 : 0,
            p: 1.25,
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
            Auto-resolved · {readOnlyEntries.length}
          </Typography>
          <Stack spacing={0.5}>
            {readOnlyEntries.map(([key, src]) => {
              const value = preview.values[key];
              const drifted = isDrifted(key);
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto auto',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {key}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                    {String(value ?? '')}
                  </Typography>
                  <Chip
                    label={SOURCE_LABEL[src]}
                    size="small"
                    color={SOURCE_CHIP_COLOR[src]}
                    variant="outlined"
                    sx={{ fontSize: 10, height: 18 }}
                  />
                  {drifted ? (
                    <Tooltip title={`Actual: ${String(actualParams[key])}`} placement="top">
                      <Chip
                        label="drifted"
                        size="small"
                        color="warning"
                        sx={{ fontSize: 10, height: 18, cursor: 'help' }}
                        aria-label={`${key} drifted — actual: ${String(actualParams[key])}`}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      label="in sync"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: 10, height: 18 }}
                      aria-label={`${key} in sync`}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {operatorInputKeys.length > 0 ? (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'warning.main', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
            Operator input · {operatorInputKeys.length}
          </Typography>
          <Stack spacing={1.5}>
            {operatorInputKeys.map((key) => {
              const drifted = isDrifted(key);
              return (
                <Box key={key}>
                  <TextField
                    label={key}
                    value={values[key] ?? ''}
                    onChange={(e) => setValue(key, e.target.value)}
                    required={unresolvedKeys.includes(key) || !desiredParams[key]}
                    error={Boolean(errors[key])}
                    helperText={errors[key] || (drifted ? `Actual: ${String(actualParams[key])}` : '')}
                    size="small"
                    fullWidth
                    disabled={submitting}
                    sx={{ '& input': { fontFamily: 'monospace' } }}
                    slotProps={{
                      input: {
                        endAdornment: values[key] ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => clearValue(key)}
                              disabled={submitting}
                              edge="end"
                              aria-label={`Clear ${key}`}
                            >
                              <ClearIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      },
                    }}
                  />
                  {drifted && !errors[key] && (
                    <Chip
                      label="drifted"
                      size="small"
                      color="warning"
                      sx={{ mt: 0.5, fontSize: 10, height: 18 }}
                      aria-label={`${key} drifted`}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={submit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={14} /> : undefined}
            >
              {submitting ? 'Saving…' : 'Save parameters'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No operator input on this product — literals and resolvers are version-owned.
        </Typography>
      )}

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
});

DeploymentRowParameters.displayName = 'DeploymentRowParameters';
export default DeploymentRowParameters;
